export interface BlogPost {
  slug: string;
  title: string;
  date: string; // YYYY-MM-DD
  modifiedDate?: string;
  category: string;
  excerpt: string;
  contentMarkdown: string; 
  imageUrl?: string;
  author?: string;
  tags?: string[];
  indexable?: boolean;
}

export const blogPosts: BlogPost[] = [
    {
        slug: "2025-swppp-regulatory-update",
        indexable: false,
        title: "URGENT: 2025 SWPPP Deadlines for NY & Federal Lands",
        date: "2025-01-28", // Set to "current" date for relevance
        category: "Regulatory Alert",
        excerpt: "New York's GP-0-25-001 is effective Jan 29. EPA's Federal Lands modification hits April 8. Here is what you need to do immediately.",
        contentMarkdown: `## The Clock is Ticking

Two major regulatory shifts are hitting the construction industry in Q1 2025. If you operate in **New York State** or on **Federal Property** (anywhere in the US), your compliance requirements are changing.

## 1. New York: GP-0-25-001 (Effective Jan 29, 2025)

The NYSDEC has issued the new SPDES General Permit for Stormwater Discharges from Construction Activity.

### The Critical Deadline
Existing projects covered under the old permit (GP-0-20-001) **DO NOT** automatically roll over.
*   **Action:** You must re-authorize your coverage.
*   **Deadline:** You have **90 days** from the effective date (Jan 29) to submit a new Notice of Intent (NOI) certification statement. 
*   **Risk:** Failure to re-certify means your permit coverage expires, and you are operating illegally.

### Key Changes
*   **"Trained Contractor" Frequency:** The requirement for daily inspections by a trained contractor remains, but documentation requirements have tightened.
*   **Enhanced Turbidity Controls:** Sites in impaired watersheds face stricter dewatering limitations.

[CTA]

## 2. EPA Federal Lands Modification (Effective April 8, 2025)

The EPA has finalized modifications to the 2022 Construction General Permit (CGP). 

### Who is impacted?
This affects "Lands of Exclusive Federal Jurisdiction." This includes:
*   Military Bases
*   National Parks
*   Federal Courthouses & Post Offices (in some cases)
*   Tribal Lands (already covered, but clarified)

### The Change
Previously, there was ambiguity about whether state or federal permits applied to certain federal enclaves within states. The modification clarifies that **EPA is the permitting authority** for these lands.

*   **Action:** If you are a contractor on federal property, check if you need to file an eNOI via the EPA's NeT system, even if the surrounding state (like Texas or California) handles its own permitting.

## Summary Checklist

1.  **NY Operators:** Log into the nForm Portal and file your recertification before April.
2.  **Federal Contractors:** Use our [2025 Applicability Checker](/resources/2025-cgp-update) to see if your jurisdiction has shifted.
3.  **Everyone:** Ensure your SWPPP binders are updated with the new permit numbers. Old permit numbers on your inspection reports are an immediate red flag during an audit.`,
        author: "Regulatory Affairs",
        tags: ["NYSDEC", "EPA", "2025 Update", "Urgent"]
    },
    {
        slug: "how-to-check-missed-swppp-inspections",
        title: "Did You Miss an Inspection? How to Retroactively Check Rainfall Data",
        date: "2025-02-02",
        category: "Compliance",
        excerpt: "The EPA doesn't accept 'I didn't know it rained' as an excuse. Use our free tool to audit your site's rainfall history against your inspection logs.",
        contentMarkdown: `## The "Phantom" Rain Event

It's Monday morning. You arrive at the jobsite, and the ground is dry. You assume the weekend was clear and skip the "post-storm" inspection required by your permit.

Two weeks later, a regulator pulls the NOAA data for your zip code. It rained 0.6 inches on Saturday night. You missed the 24-hour window. **That is a violation.**

## The 0.5-Inch Rule

Most Construction General Permits (including the Federal EPA CGP and Texas TPDES) require inspections within 24 hours of a rain event producing **0.5 inches or greater**. Some states (like California) have even stricter triggers.

If you are managing multiple sites across town, it is impossible to know exactly how much rain fell at every specific location without a digital gauge.

## How to Audit Your History

Before an inspector does it for you, you should self-audit your logs against historical weather data.

We built a free tool to help you do exactly this:

[CTA]

## Using the Tool
1.  Enter your jobsite address or zip code.
2.  Our system queries the Open-Meteo historical archive.
3.  We highlight every day in the last 30 days where precipitation exceeded **0.5 inches**.

Compare these dates against your SWPPP binder. If you have a gap, document it immediately in a Corrective Action Log. 

## The Solution: Automated Monitoring

Checking history is damage control. **Real-time alerts are prevention.**

RunOffTrack automates this entire process. We geofence your site and ping you the moment a rain threshold is crossed. We even generate the inspection form for you automatically.

Don't let a surprise storm cost you a fine.`,
        author: "Compliance Desk",
        tags: ["Tools", "Rainfall", "Audits"]
    },
    {
        slug: "digital-swppp-compliance-2025",
        indexable: false,
        title: "The Cost of Non-Compliance: Why Digital SWPPP Inspections are Essential in 2025",
        date: "2025-01-10",
        category: "Compliance",
        excerpt: "Manual checklists are costing you money and risking EPA fines. Discover how digital workflows protect your projects and streamline reporting.",
        contentMarkdown: `## The Paperwork Trap

For decades, the construction industry has relied on clipboards, wet signatures, and physical binders to manage Stormwater Pollution Prevention Plans (SWPPP). While the regulations have evolved, the tools often haven't. In 2025, relying on paper isn't just inefficient—it's a liability.

The EPA and state environmental agencies are increasingly data-driven. When an auditor arrives on site, fumbling through a muddy binder to find a missing inspection report from three weeks ago is the fastest way to trigger a deeper investigation.

## The Financial Risk

The Clean Water Act empowers the EPA to levy fines of up to **$64,618 per day, per violation**. 

A "violation" isn't just a sediment leak. It can be:
*   Missing a weekly inspection.
*   Failing to inspect within 24 hours of a rain event.
*   Illegible or missing signatures on a report.
*   Failure to document corrective actions.

If you miss inspections for two weeks on a site, that could theoretically be 14 days of violations. The math gets scary, fast.

## Why Digital?

Digital SWPPP management tools like **RunOffTrack** solve the core problems of paper-based compliance:

### 1. Automated "Rain Event" Triggers
Instead of checking the weather manually, digital tools pull hyper-local NOAA data. If it rains 0.5 inches overnight, your inspector gets a notification that an inspection is due.

### 2. Forced Compliance
Digital forms can require photos for failed BMPs and prevent submission without a signature. This ensures every report is complete before it's filed.

### 3. Instant Audit Trails
Cloud storage means your reports are searchable and accessible instantly. When an inspector asks for the last 6 months of reports, you can email a PDF package in seconds.

[CTA]

## Conclusion

The transition to digital isn't just about saving paper; it's about risk mitigation. In an era of heightened scrutiny and increasing construction volume, a robust digital workflow is your best insurance policy against regulatory action.`,
        author: "RunOffTrack Team",
        tags: ["Compliance", "EPA", "Construction Tech"]
    },
    {
        slug: "swppp-inspection-frequency-guide",
        title: "SWPPP Inspection Frequency: When Do You Actually Need to Inspect?",
        date: "2025-01-15",
        category: "Guides",
        excerpt: "7 days? 14 days? Rain events? We break down the confusing inspection frequency rules for Texas (TCEQ), California (CGP), and EPA federal permits.",
        contentMarkdown: `## The Confusion of "Routine" vs. "Event-Based"

One of the most common questions we get is: "How often do I really need to send someone out there?" The answer depends entirely on your specific Construction General Permit (CGP), but there are general rules of thumb.

## Standard Frequencies

Most permits offer two main tracks:

1.  **The 7-Day Rule:** Inspect every 7 calendar days, regardless of weather.
2.  **The 14-Day + Rain Rule:** Inspect every 14 calendar days, PLUS within 24 hours of a rain event producing 0.25 inches or more.

### Which is better?
*   **High-Rainfall Areas (e.g., Florida, Pacific NW):** The **7-Day Rule** is often safer. If you choose the rain event track, you might end up inspecting 3 times a week during the wet season.
*   **Arid Areas (e.g., Arizona, West Texas):** The **14-Day Rule** saves significant labor costs.

## Tracking Rain Events

The biggest compliance gap we see is missing the "post-storm" inspection window. 

> **Pro Tip:** You cannot rely on the weather app on your phone. You need site-specific data. 

**RunOffTrack** automates this by geofencing your project and logging daily precipitation totals from NOAA. We alert you when a threshold is crossed.

[CTA]

## State Specifics

*   **Texas (TCEQ):** Strict 0.5-inch trigger.
*   **California (SWRCB):** Requires "Pre-Storm", "During Storm", and "Post-Storm" inspections for Risk Level 2 & 3 sites.
*   **Georgia (EPD):** Requires daily rainfall logging even if no inspection occurs.

Check our [State Directory](/swppp-requirements) for your specific local requirements.`,
        author: "Compliance Desk",
        tags: ["Regulations", "TCEQ", "CGP"]
    },
    {
        slug: "common-bmp-failures-silt-fence",
        modifiedDate: "2026-09-12",
        title: "Top 3 Silt Fence Failures (And How to Fix Them)",
        date: "2025-01-20",
        category: "Best Practices",
        excerpt: "Silt fences are the most common BMP, and the most commonly failed. Learn how to spot undercutting and installation errors before the inspector does.",
        contentMarkdown: `## The "Black Ribbon" of Compliance

Drive by any construction site and you'll see it: thousands of feet of black geotextile fabric. Silt fence is the first line of defense, but it's often installed and forgotten.

Here are three common failure patterns and the field checks that expose them:

### 1. Undercutting (The #1 Offender)
**The problem:** Water flows *under* the fence instead of ponding behind it. Look for a soil-stained flow path, exposed fabric, or an open seam at grade.

**The repair:** Reinstall the affected run so the bottom of the fabric is keyed into the soil and the backfill is compacted. EPA's construction SWPPP guide illustrates a trench at least 6 inches deep and 6 inches wide, but the approved plan and local specification control for your site.

### 2. Overtopping
**The problem:** Sediment reduces storage, fabric sags, or runoff overtops the fence. Overtopping can collapse posts and release accumulated sediment at once.

**The repair:** Remove sediment before it reaches one-third of the fence height, or sooner if the fabric is under strain. Replace torn fabric and reset leaning or loose posts. EPA guidance uses the one-third threshold for silt fence; do not confuse it with the separate one-half-height guidance for fiber rolls.

### 3. End-Arounds
**The problem:** The fence ends at a lower elevation, allowing runoff to bypass the control. Erosion at either end is the telltale sign.

**The repair:** Extend and turn the end up-gradient so the barrier can contain and spread runoff. Never place silt fence in a ditch, channel, stream, or other concentrated-flow path; select a control designed for that flow instead.

## A Fast Inspection Sequence

1. Walk the full receiving side and look for off-site sediment.
2. Check the soil-to-fabric seal and every overlap or joint.
3. Check posts, attachment points, tears, sagging, and overtopping marks.
4. Measure sediment accumulation rather than estimating from a distance.
5. Inspect both ends for bypass and confirm the fence follows the intended contour.
6. Assign each repair an owner and completion deadline under the applicable permit.

## Documenting Repairs

Record the observed condition, location, date, corrective action, responsible person, and completion date. Take before-and-after photos when useful, but follow the inspection-report and corrective-action deadlines in the permit that covers your project.

See our [BMP Field Guide](/resources/bmp-field-guide/silt-fence) for a complete visual checklist of silt fence requirements.

Primary reference: [EPA's Developing Your Stormwater Pollution Prevention Plan guide](https://www.epa.gov/sites/default/files/2015-10/documents/sw_swppp_guide.pdf). Requirements and design details can vary by state, municipality, approved erosion-control plan, and manufacturer.

*Official source last reviewed September 12, 2026.*

[CTA]`,
        author: "RunOffTrack",
        tags: ["BMPs", "Maintenance", "Silt Fence"]
    },
    {
        slug: "epa-fine-risk-calculator-explained",
        indexable: false,
        title: "Understanding EPA Fines: How We Calculate Your Risk",
        date: "2025-01-25",
        category: "Risk Management",
        excerpt: "The EPA increased maximum civil penalties in 2024. We explain the math behind potential fines and how settlement negotiations typically work.",
        contentMarkdown: `## The Sticker Shock

In 2024, the EPA adjusted its maximum civil penalty for Clean Water Act violations to **$64,618 per day, per violation**. 

Most contractors think, "They won't fine me that much for a missing signature." And usually, they are right—the *maximum* is rarely applied for minor administrative errors. However, the *threat* of the maximum is what gives the EPA leverage.

## How the Calculator Works

Our [Fine Risk Calculator](/tools/fine-calculator) uses a simplified model based on enforcement history:

1.  **Duration of Non-Compliance:** How long has the site been active without proper records?
2.  **Inspection Frequency:** How many reports *should* exist vs. how many *actually* exist?
3.  **Severity Factor:** We apply a conservative "settlement factor" (usually 10-20% of the max) to give you a realistic "bad day" scenario.

## The Multiplier Effect

The danger isn't one missing report. It's the multiplier.

*   1 missing report = 1 violation.
*   1 missing report x 7 days late = 7 days of non-compliance.
*   10 missing reports over a 6 month project? You are now looking at potential six-figure settlements.

## Avoidance Strategy

The only way to zero out this risk is automation. If the software sends the reminder, generates the form, and stores the PDF, you eliminate the "human error" gap that leads to fines.

[CTA]`,
        author: "Legal Team",
        tags: ["Fines", "EPA", "Business Risk"]
    }
];
